import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import { useParams, useNavigate } from 'react-router-dom';
import { DEV_MODE } from '../../authConfig';
import { useDevAuth } from '../DevAuthProvider';
import { useApiAuth } from '../../hooks/useApiAuth';
import { laceToolAPI, AssessmentData } from '../../services/api';
import { authenticatedLaceToolAPI } from '../../services/authenticatedApi';
import { facilityAPI } from '../../services/facilityApi';
import { type Facility } from '../../types/facility';
import { SkeletonText } from '../Skeleton/SkeletonLoader';
import { 
  Save, 
  Info, 
  User, 
  Building, 
  Calendar,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';

const FormContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 32px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const FormLogo = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 10px;
  background: white;
  padding: 6px;
  flex-shrink: 0;
`;

const FormHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const FormTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const FormSubtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
`;

const FormContent = styled.div`
  padding: 32px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;

  &.required::after {
    content: ' *';
    color: #ef4444;
    font-weight: bold;
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  background: #f9fafb;
  transition: all 0.2s ease;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #d1d5db;
  }

  &::placeholder {
    color: #9ca3af;
    font-style: italic;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  background: #f9fafb;
  transition: all 0.2s ease;
  font-weight: 500;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #d1d5db;
  }
`;

const FacilitySearchContainer = styled.div`
  position: relative;
`;

const FacilityDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

const FacilityOption = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FacilityName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
`;

const FacilityLocation = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ScoringSection = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
`;

const ScoringTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ScoringRow = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ScoringRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ScoringRowTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PointsDisplay = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 14px;
  min-width: 60px;
  text-align: center;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const OptionCard = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 10px;
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #3b82f6;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
`;

const CheckboxCard = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
`;

const ComorbidityNote = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
`;

const ResultsSection = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #22c55e;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ResultValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const ResultLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RiskIndicator = styled.div<{ risk: 'low' | 'moderate' | 'high' | 'very-high' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;

  ${props => {
    switch (props.risk) {
      case 'low':
        return 'background: #d1fae5; color: #065f46;';
      case 'moderate':
        return 'background: #fef3c7; color: #92400e;';
      case 'high':
        return 'background: #fed7aa; color: #9a3412;';
      case 'very-high':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #e5e7eb;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid;
  min-width: 200px;
  justify-content: center;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: white;
    border-color: #22c55e;
    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);

    &:hover {
      background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const MessageContainer = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 16px 20px;
  border-radius: 10px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;

  ${props => {
    switch (props.type) {
      case 'success':
        return 'background: #d1fae5; color: #065f46; border: 1px solid #22c55e;';
      case 'error':
        return 'background: #fee2e2; color: #991b1b; border: 1px solid #ef4444;';
      case 'info':
        return 'background: #dbeafe; color: #1e40af; border: 1px solid #3b82f6;';
      default:
        return 'background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;';
    }
  }}
`;

const InfoButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
  }
`;

interface FormData {
  patientName: string;
  facilityName: string;
  dob: string;
  admissionDate: string;
  lengthOfStay: string;
  acuteAdmission: string;
  comorbidities: string[];
  emergencyVisits: string;
  assessedBy: string;
  notes: string;
}

interface Option {
  value: string;
  label: string;
  points: number;
}

const LaceToolFormModern: React.FC = () => {
  const { accounts } = useMsal();
  const devAuth = DEV_MODE ? useDevAuth() : null;
  const { getAccessToken } = useApiAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<FormData>({
    patientName: '',
    facilityName: '',
    dob: '',
    admissionDate: '',
    lengthOfStay: '',
    acuteAdmission: '',
    comorbidities: [],
    emergencyVisits: '',
    assessedBy: '',
    notes: ''
  });

  const [facilitySearch, setFacilitySearch] = useState('');
  const [showFacilityDropdown, setShowFacilityDropdown] = useState(false);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);

  // Load facilities from API
  const loadFacilities = async () => {
    setLoadingFacilities(true);
    try {
      const response = await facilityAPI.getFacilities({ limit: 100 }); // Get all facilities
      setFacilities(response.facilities);
    } catch (error) {
      console.error('Error loading facilities:', error);
      setFacilities([]);
    } finally {
      setLoadingFacilities(false);
    }
  };

  const loadAssessmentData = async (assessmentId: string) => {
    setIsLoading(true);
    try {
      const assessment = await authenticatedLaceToolAPI.getAssessment(assessmentId);
      
      // Reverse mapping functions to convert database values back to form values
      const reverseMapLengthOfStay = (value: string): string => {
        const mapping: Record<string, string> = {
          '1 day': '1day',
          '2 days': '2days',
          '3 days': '3days',
          '4-6 days': '4-6days',
          '7-13 days': '7-13days',
          '14+ days': '14plus'
        };
        return mapping[value] || '1day';
      };

      const reverseMapAcuteAdmission = (value: string): string => {
        const mapping: Record<string, string> = {
          'Yes': 'inpatient',
          'No': 'observation'
        };
        return mapping[value] || 'observation';
      };

      const reverseMapComorbidity = (value: string): string | null => {
        const mapping: Record<string, string | null> = {
          'Myocardial infarction': 'mi',
          'Cerebrovascular disease': 'cerebrovascular',
          'Peripheral vascular disease': 'peripheral',
          'Diabetes without complications': 'diabetes',
          'Congestive heart failure': 'chf',
          'Diabetes with complications': 'diabetes_complications',
          'Chronic pulmonary disease': 'copd',
          'Mild liver disease': 'liver_mild',
          'Tumor without metastasis': 'tumor',
          'Dementia': 'dementia',
          'Connective tissue disease': 'connective',
          'AIDS': 'aids',
          'Moderate to severe liver disease': 'liver_severe',
          'Metastatic solid tumor': 'metastatic'
        };
        return mapping[value] || null;
      };

      const reverseMapEmergencyVisits = (value: string): string => {
        const mapping: Record<string, string> = {
          '0': '0',
          '1': '1',
          '2': '2',
          '3': '3',
          '4+': '4plus'
        };
        return mapping[value] || '0';
      };

      // Map comorbidities from database format to form format
      const mappedComorbidities = (assessment.comorbidities || [])
        .map(reverseMapComorbidity)
        .filter((c): c is string => c !== null);
      
      // Populate form with existing data
      setFormData({
        patientName: assessment.patientName,
        facilityName: assessment.facilityName,
        dob: assessment.dateOfBirth,
        admissionDate: assessment.admissionDate,
        lengthOfStay: reverseMapLengthOfStay(assessment.lengthOfStay),
        acuteAdmission: reverseMapAcuteAdmission(assessment.acuteAdmission),
        comorbidities: mappedComorbidities,
        emergencyVisits: reverseMapEmergencyVisits(assessment.emergencyVisits),
        assessedBy: assessment.assessedBy || '',
        notes: assessment.notes || ''
      });
      
      setFacilitySearch(assessment.facilityName);
    } catch (error) {
      console.error('Error loading assessment data:', error);
      setSubmitMessage('Failed to load assessment data. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Load facilities on component mount
  useEffect(() => {
    loadFacilities();
  }, []);

  // Load existing assessment data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadAssessmentData(id);
    }
  }, [isEditMode, id]);

  // Filter facilities based on search
  const filteredFacilities = facilities.filter(facility =>
    facility.name.toLowerCase().includes(facilitySearch.toLowerCase()) ||
    facility.address.city.toLowerCase().includes(facilitySearch.toLowerCase())
  );

  const handleFacilitySelect = (facilityName: string) => {
    setFormData(prev => ({ ...prev, facilityName }));
    setFacilitySearch(facilityName);
    setShowFacilityDropdown(false);
  };

  const handleFacilitySearchChange = (value: string) => {
    setFacilitySearch(value);
    setFormData(prev => ({ ...prev, facilityName: value }));
    setShowFacilityDropdown(value.length > 0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-facility-search]')) {
        setShowFacilityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const lengthOfStayOptions: Option[] = [
    { value: 'less1', label: 'Less than 1 Day', points: 0 },
    { value: '1day', label: '1 Day', points: 1 },
    { value: '2days', label: '2 Days', points: 2 },
    { value: '3days', label: '3 Days', points: 3 },
    { value: '4-6days', label: '4-6 Days', points: 4 },
    { value: '7-13days', label: '7-13 Days', points: 5 },
    { value: '14plus', label: '14 or more Days', points: 6 }
  ];

  const acuteAdmissionOptions: Option[] = [
    { value: 'inpatient', label: 'Inpatient', points: 3 },
    { value: 'observation', label: 'Observation', points: 0 }
  ];

  const comorbidityOptions: Option[] = [
    { value: 'none', label: 'No Prior History', points: 0 },
    { value: 'mi', label: 'Previous MI (Myocardial Infarction)', points: 1 },
    { value: 'cerebrovascular', label: 'Cerebrovascular Disease', points: 1 },
    { value: 'peripheral', label: 'Peripheral Vascular Disease', points: 1 },
    { value: 'diabetes', label: 'Diabetes without Complications', points: 1 },
    { value: 'chf', label: 'CHF (Congestive Heart Failure)', points: 2 },
    { value: 'diabetes_complications', label: 'Diabetes with End Organ Damage', points: 2 },
    { value: 'copd', label: 'COPD', points: 2 },
    { value: 'liver_mild', label: 'Mild Liver or Renal Disease', points: 2 },
    { value: 'tumor', label: 'Any Tumor (Including lymphoma or leukemia)', points: 2 },
    { value: 'dementia', label: 'Dementia', points: 3 },
    { value: 'connective', label: 'Connective Tissue Disease', points: 3 },
    { value: 'aids', label: 'AIDS', points: 4 },
    { value: 'liver_severe', label: 'Moderate or Severe Liver or Renal Disease', points: 4 },
    { value: 'metastatic', label: 'Metastatic Cancer', points: 6 }
  ];

  const emergencyVisitOptions: Option[] = [
    { value: '0', label: '0 Visits', points: 0 },
    { value: '1', label: '1 Visit', points: 1 },
    { value: '2', label: '2 Visits', points: 2 },
    { value: '3', label: '3 Visits', points: 3 },
    { value: '4plus', label: '4 or more Visits', points: 4 }
  ];

  const calculateTotalScore = (): number => {
    let total = 0;
    
    const losOption = lengthOfStayOptions.find(opt => opt.value === formData.lengthOfStay);
    if (losOption) total += losOption.points;
    
    const admissionOption = acuteAdmissionOptions.find(opt => opt.value === formData.acuteAdmission);
    if (admissionOption) total += admissionOption.points;
    
    let comorbidityPoints = 0;
    formData.comorbidities.forEach(comorbidity => {
      const option = comorbidityOptions.find(opt => opt.value === comorbidity);
      if (option) comorbidityPoints += option.points;
    });
    total += Math.min(comorbidityPoints, 6);
    
    const emergencyOption = emergencyVisitOptions.find(opt => opt.value === formData.emergencyVisits);
    if (emergencyOption) total += emergencyOption.points;
    
    return total;
  };

  const getReadmissionRisk = (score: number): { level: string; risk: 'low' | 'moderate' | 'high' | 'very-high' } => {
    if (score <= 4) return { level: 'Low Risk', risk: 'low' };
    if (score <= 6) return { level: 'Moderate Risk', risk: 'moderate' };
    if (score <= 9) return { level: 'High Risk', risk: 'high' };
    return { level: 'Very High Risk', risk: 'very-high' };
  };

  const getReadmissionProbability = (score: number): string => {
    const probabilityMap: { [key: number]: string } = {
      0: '0%',
      1: '1%',
      2: '2%',
      3: '3%',
      4: '4%',
      5: '5%',
      6: '6%',
      7: '7%',
      8: '9%',
      9: '10%',
      10: '12%',
      11: '14%',
      12: '17%',
      13: '20%',
      14: '23%',
      15: '27%',
      16: '30%',
      17: '35%',
      18: '39%',
      19: '44%'
    };
    
    // For scores above 19, use the highest known probability
    if (score >= 19) return '44%+';
    
    return probabilityMap[score] || 'Unknown';
  };

  const handleInputChange = (field: keyof FormData, value: string): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear any existing validation messages when user starts typing
    if (submitMessage && messageType === 'error') {
      setSubmitMessage(null);
      setMessageType(null);
    }
  };

  const handleComorbidityChange = (comorbidity: string, checked: boolean): void => {
    setFormData(prev => ({
      ...prev,
      comorbidities: checked 
        ? [...prev.comorbidities, comorbidity]
        : prev.comorbidities.filter(c => c !== comorbidity)
    }));
  };

  // Function to make authenticated API call
  const makeAuthenticatedApiCall = async (assessmentData: AssessmentData): Promise<any> => {
    try {
      console.log('🔍 makeAuthenticatedApiCall started');
      
      // Get access token using the consistent auth hook
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Unable to acquire access token. Please try logging in again.');
      }

      console.log('🔍 Making API call with token:', token.substring(0, 20) + '...');

      // Use authenticated API service
      console.log('🔍 Request payload:', assessmentData);
      
      const responseData = await authenticatedLaceToolAPI.createAssessment(assessmentData);
      console.log('🔍 API Response data:', responseData);
      return responseData;

    } catch (error: any) {
      console.error('🔍 Error in makeAuthenticatedApiCall:', error);
      throw error;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    setSubmitMessage(null);
    setMessageType(null);

    const requiredFields: (keyof FormData)[] = ['patientName', 'facilityName', 'dob', 'admissionDate', 'lengthOfStay', 'acuteAdmission', 'emergencyVisits'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setSubmitMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setMessageType('error');
      setIsSubmitting(false);
      return;
    }

    // Validate that date of birth is not in the future
    if (formData.dob) {
      const today = new Date();
      const dobDate = new Date(formData.dob);
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      dobDate.setHours(0, 0, 0, 0);
      
      if (dobDate > today) {
        setSubmitMessage('Date of birth cannot be in the future');
        setMessageType('error');
        setIsSubmitting(false);
        return;
      }
    }

    // Validate that date of birth and admission date are not the same
    if (formData.dob && formData.admissionDate && formData.dob === formData.admissionDate) {
      setSubmitMessage('Date of birth and admission date cannot be the same');
      setMessageType('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const currentUser = DEV_MODE ? devAuth?.user : accounts[0];
      const assessedBy = formData.assessedBy || (currentUser ? 
        (currentUser.name || currentUser.username || 'Unknown User') : 
        'Unknown User');

      const mapLengthOfStay = (value: string): string => {
        const mapping: Record<string, string> = {
          'less1': '1 day',
          '1day': '1 day',
          '2days': '2 days',
          '3days': '3 days',
          '4-6days': '4-6 days',
          '7-13days': '7-13 days',
          '14plus': '14+ days'
        };
        return mapping[value] || '1 day';
      };

      const mapAcuteAdmission = (value: string): string => {
        const mapping: Record<string, string> = {
          'inpatient': 'Yes',
          'observation': 'No'
        };
        return mapping[value] || 'No';
      };

      const mapComorbidity = (value: string): string | null => {
        const mapping: Record<string, string | null> = {
          'none': null,
          'mi': 'Myocardial infarction',
          'cerebrovascular': 'Cerebrovascular disease',
          'peripheral': 'Peripheral vascular disease',
          'diabetes': 'Diabetes without complications',
          'chf': 'Congestive heart failure',
          'diabetes_complications': 'Diabetes with complications',
          'copd': 'Chronic pulmonary disease',
          'liver_mild': 'Mild liver disease',
          'tumor': 'Tumor without metastasis',
          'dementia': 'Dementia',
          'connective': 'Connective tissue disease',
          'aids': 'AIDS',
          'liver_severe': 'Moderate to severe liver disease',
          'metastatic': 'Metastatic solid tumor'
        };
        return mapping[value];
      };

      const mapEmergencyVisits = (value: string): string => {
        const mapping: Record<string, string> = {
          '0': '0',
          '1': '1',
          '2': '2',
          '3': '3',
          '4plus': '4+'
        };
        return mapping[value] || '0';
      };

      const mappedComorbidities = formData.comorbidities
        .map(mapComorbidity)
        .filter(comorbidity => comorbidity !== null);

      const assessmentData: AssessmentData = {
        patientName: formData.patientName,
        facilityName: formData.facilityName,
        dateOfBirth: formData.dob,
        admissionDate: formData.admissionDate,
        lengthOfStay: mapLengthOfStay(formData.lengthOfStay),
        acuteAdmission: mapAcuteAdmission(formData.acuteAdmission),
        comorbidities: mappedComorbidities,
        emergencyVisits: mapEmergencyVisits(formData.emergencyVisits),
        assessedBy: assessedBy,
        notes: formData.notes || `Assessment ${isEditMode ? 'updated' : 'completed'} on ${new Date().toLocaleDateString()}`
      };

      if (isEditMode && id) {
        // Update existing assessment
        await authenticatedLaceToolAPI.updateAssessment(id, assessmentData);
        setSubmitMessage('Assessment updated successfully!');
        setMessageType('success');
        toast.success('Assessment updated successfully!');
        
        // Navigate back to assessments list after successful update
        setTimeout(() => {
          navigate('/assessments');
        }, 2000);
      } else {
        // Create new assessment
        await authenticatedLaceToolAPI.createAssessment(assessmentData);
        setSubmitMessage('Assessment saved successfully!');
        setMessageType('success');
        toast.success('Assessment created successfully!');
        
        // Scroll to top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Reset form after successful creation
        setTimeout(() => {
          setFormData({
            patientName: '',
            facilityName: '',
            dob: '',
            admissionDate: '',
            lengthOfStay: '',
            acuteAdmission: '',
            comorbidities: [],
            emergencyVisits: '',
            assessedBy: '',
            notes: ''
          });
          setFacilitySearch('');
          setSubmitMessage(null);
          setMessageType(null);
        }, 3000);
      }

    } catch (error: any) {
      console.error('Error submitting assessment:', error);
      
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'save'} assessment. Please try again.`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitMessage(errorMessage);
      setMessageType('error');
      toast.error(errorMessage);
      
      setTimeout(() => {
        setSubmitMessage(null);
        setMessageType(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalScore = calculateTotalScore();
  const riskInfo = getReadmissionRisk(totalScore);

  return (
    <FormContainer>
      <FormHeader>
        <FormLogo 
          src="https://www.chcahealth.org/wp-content/uploads/2023/12/main-logop.png" 
          alt="CHCA Logo"
        />
        <FormHeaderContent>
          <FormTitle>
            <Activity size={32} />
            {isEditMode ? 'Edit Assessment' : 'LACE Tool Assessment'}
          </FormTitle>
          <FormSubtitle>
            Length of stay, Acuity of admission, Comorbidities, Emergency visits
          </FormSubtitle>
        </FormHeaderContent>
      </FormHeader>

      <FormContent>
        {isLoading && (
          <MessageContainer type="info">
            <Info size={20} />
            Loading assessment data...
          </MessageContainer>
        )}
        
        {submitMessage && messageType && (
          <MessageContainer type={messageType}>
            {messageType === 'success' ? <CheckCircle size={20} /> : 
             messageType === 'error' ? <AlertTriangle size={20} /> : 
             <Info size={20} />}
            {submitMessage}
          </MessageContainer>
        )}

        <Section>
          <SectionHeader>
            <SectionIcon>
              <User size={20} />
            </SectionIcon>
            <SectionTitle>Patient Information</SectionTitle>
          </SectionHeader>
          
          <FormGrid>
            <InputGroup>
              <Label className="required">Patient Name</Label>
              <Input
                type="text"
                value={formData.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                placeholder="Enter patient full name"
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label className="required">Facility Name</Label>
              <FacilitySearchContainer data-facility-search>
                <Input
                  type="text"
                  value={facilitySearch}
                  onChange={(e) => handleFacilitySearchChange(e.target.value)}
                  onFocus={() => setShowFacilityDropdown(true)}
                  placeholder="Search or select a facility..."
                  required
                />
                {showFacilityDropdown && (
                  <FacilityDropdown>
                    {loadingFacilities ? (
                      <FacilityOption>
                        <SkeletonText lines={1} width="150px" />
                      </FacilityOption>
                    ) : filteredFacilities.length > 0 ? (
                      filteredFacilities.slice(0, 10).map((facility) => (
                        <FacilityOption
                          key={facility.id}
                          onClick={() => handleFacilitySelect(facility.name)}
                        >
                          <FacilityName>{facility.name}</FacilityName>
                          <FacilityLocation>
                            {facility.address.city}, {facility.address.state}
                          </FacilityLocation>
                        </FacilityOption>
                      ))
                    ) : (
                      <FacilityOption>
                        <FacilityName>No facilities found</FacilityName>
                      </FacilityOption>
                    )}
                  </FacilityDropdown>
                )}
              </FacilitySearchContainer>
            </InputGroup>
            
            <InputGroup>
              <Label className="required">Date of Birth</Label>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label className="required">Admission Date</Label>
              <Input
                type="date"
                value={formData.admissionDate}
                onChange={(e) => handleInputChange('admissionDate', e.target.value)}
                required
              />
            </InputGroup>
            
            {isEditMode && (
              <InputGroup>
                <Label>Assessed By</Label>
                <Input
                  type="text"
                  value={formData.assessedBy}
                  onChange={(e) => handleInputChange('assessedBy', e.target.value)}
                  placeholder="Enter health care provider name"
                />
              </InputGroup>
            )}
            
            <InputGroup style={{ gridColumn: '1 / -1' }}>
              <Label>Notes</Label>
              <Input
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes..."
              />
            </InputGroup>
          </FormGrid>
        </Section>

        <Section>
          <SectionHeader>
            <SectionIcon>
              <FileText size={20} />
            </SectionIcon>
            <SectionTitle>LACE Scoring</SectionTitle>
          </SectionHeader>

          <ScoringSection>
            <ScoringTable>
              <ScoringRow>
                <ScoringRowHeader>
                  <ScoringRowTitle>
                    <Clock size={18} />
                    Length of Stay (L)
                  </ScoringRowTitle>
                  <PointsDisplay>
                    {lengthOfStayOptions.find(opt => opt.value === formData.lengthOfStay)?.points || 0}
                  </PointsDisplay>
                </ScoringRowHeader>
                <OptionsGrid>
                  {lengthOfStayOptions.map(option => (
                    <OptionCard
                      key={option.value}
                      selected={formData.lengthOfStay === option.value}
                    >
                      <RadioInput
                        type="radio"
                        name="lengthOfStay"
                        value={option.value}
                        checked={formData.lengthOfStay === option.value}
                        onChange={(e) => handleInputChange('lengthOfStay', e.target.value)}
                      />
                      {option.label}
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </ScoringRow>

              <ScoringRow>
                <ScoringRowHeader>
                  <ScoringRowTitle>
                    <Building size={18} />
                    Acute Admission (A)
                  </ScoringRowTitle>
                  <PointsDisplay>
                    {acuteAdmissionOptions.find(opt => opt.value === formData.acuteAdmission)?.points || 0}
                  </PointsDisplay>
                </ScoringRowHeader>
                <OptionsGrid>
                  {acuteAdmissionOptions.map(option => (
                    <OptionCard
                      key={option.value}
                      selected={formData.acuteAdmission === option.value}
                    >
                      <RadioInput
                        type="radio"
                        name="acuteAdmission"
                        value={option.value}
                        checked={formData.acuteAdmission === option.value}
                        onChange={(e) => handleInputChange('acuteAdmission', e.target.value)}
                      />
                      {option.label}
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </ScoringRow>

              <ScoringRow>
                <ScoringRowHeader>
                  <ScoringRowTitle>
                    <Activity size={18} />
                    Comorbidity (C)
                  </ScoringRowTitle>
                  <PointsDisplay>
                    {Math.min(
                      formData.comorbidities.reduce((total, comorbidity) => {
                        const option = comorbidityOptions.find(opt => opt.value === comorbidity);
                        return total + (option ? option.points : 0);
                      }, 0),
                      6
                    )}
                  </PointsDisplay>
                </ScoringRowHeader>
                <ComorbidityNote>
                  Comorbidity points are cumulative to a maximum of 6 points
                </ComorbidityNote>
                <CheckboxGrid>
                  {comorbidityOptions.map(option => (
                    <CheckboxCard
                      key={option.value}
                      selected={formData.comorbidities.includes(option.value)}
                    >
                      <CheckboxInput
                        type="checkbox"
                        checked={formData.comorbidities.includes(option.value)}
                        onChange={(e) => handleComorbidityChange(option.value, e.target.checked)}
                      />
                      {option.label}
                    </CheckboxCard>
                  ))}
                </CheckboxGrid>
              </ScoringRow>

              <ScoringRow>
                <ScoringRowHeader>
                  <ScoringRowTitle>
                    <AlertTriangle size={18} />
                    Emergency Room Visits (E)
                  </ScoringRowTitle>
                  <PointsDisplay>
                    {emergencyVisitOptions.find(opt => opt.value === formData.emergencyVisits)?.points || 0}
                  </PointsDisplay>
                </ScoringRowHeader>
                <OptionsGrid>
                  {emergencyVisitOptions.map(option => (
                    <OptionCard
                      key={option.value}
                      selected={formData.emergencyVisits === option.value}
                    >
                      <RadioInput
                        type="radio"
                        name="emergencyVisits"
                        value={option.value}
                        checked={formData.emergencyVisits === option.value}
                        onChange={(e) => handleInputChange('emergencyVisits', e.target.value)}
                      />
                      {option.label}
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </ScoringRow>
            </ScoringTable>
          </ScoringSection>
        </Section>

        <ResultsSection>
          <ResultsGrid>
            <ResultCard>
              <ResultValue>{totalScore}</ResultValue>
              <ResultLabel>Total LACE Score</ResultLabel>
            </ResultCard>
            <ResultCard>
              <RiskIndicator risk={riskInfo.risk}>
                {riskInfo.level}
              </RiskIndicator>
              <ResultLabel style={{ marginTop: '12px' }}>Readmission Risk</ResultLabel>
            </ResultCard>
          </ResultsGrid>
          
          <ResultCard style={{ marginTop: '16px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #0ea5e9' }}>
            <ResultLabel style={{ color: '#0369a1', marginBottom: '8px' }}>Expected Probability of Readmission</ResultLabel>
            <ResultValue style={{ color: '#0c4a6e', fontSize: '28px' }}>{getReadmissionProbability(totalScore)}</ResultValue>
          </ResultCard>
        </ResultsSection>

        <ActionSection>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            <Save size={20} />
            {isSubmitting ? 
              (isEditMode ? 'Updating Assessment...' : 'Saving Assessment...') : 
              (isEditMode ? 'Update Assessment' : 'Save Assessment')
            }
          </Button>
        </ActionSection>
      </FormContent>

      <InfoButton onClick={() => setShowInstructions(!showInstructions)}>
        <Info size={24} />
      </InfoButton>
    </FormContainer>
  );
};

export default LaceToolFormModern;
